import React, { useMemo, useState } from 'react';
import SearchIcon from '@material-ui/icons/Search';
import {
    Card,
    Breadcrumbs,
    Link,
    Typography,
    CardContent,
    Container,
    Grid,
    Backdrop,
    CircularProgress,
    Button,
    Snackbar,
    ThemeProvider,
    TextField,
    Checkbox,
    FormControlLabel,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';
import Page from 'src/components/Page';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TablePagination from '@material-ui/core/TablePagination';
import moment from 'moment';
import Alert from 'src/components/Alert';
import trLocale from 'date-fns/locale/tr';
import DateFnsUtils from '@date-io/date-fns';
import { createMuiTheme } from '@material-ui/core/styles';
import {
    KeyboardDatePicker,
    MuiPickersUtilsProvider
} from '@material-ui/pickers';
import { getWorkOrderUrgentCustomers } from 'src/store/api/externalSystemServices';

const localeMap = {
    tr: trLocale
};

const locale = localStorage.getItem('i18nextLng');
const siyah = '#424242';

const materialTheme = createMuiTheme({
    palette: {
        primary: {
            contrastText: '#FFFFFF',
            dark: siyah,
            main: siyah,
            light: siyah
        }
    }
});

const useStyles = makeStyles(theme => ({
    backdrop: {
        zIndex: 1500,
        color: '#fff'
    },
    container: {
        maxWidth: '100%',
        overflowX: 'auto'
    },
    table: {
        minWidth: 700
    },
    searchButton: {
        backgroundColor: '#1E5128',
        color: 'white',
        height: 40
    },
    workOrderToggle: {
        height: '100%',
        display: 'flex',
        alignItems: 'center'
    }
}));

const parseUrgentCustomersResponse = (rawData) => {
    if (Array.isArray(rawData)) {
        return rawData;
    }

    if (typeof rawData === 'string') {
        try {
            const parsed = JSON.parse(rawData);
            return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
            return [];
        }
    }

    return [];
};

const formatDate = (dateValue) => {
    if (!dateValue) {
        return '-';
    }

    const formatted = moment(dateValue);
    return formatted.isValid() ? formatted.format('YYYY-MM-DD') : '-';
};

const WorkOrderUrgentCustomers = () => {
    const classes = useStyles();
    const { t } = useTranslation('overview');
    const [loading, setLoading] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [rows, setRows] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [useWorkOrderIdFilter, setUseWorkOrderIdFilter] = useState(false);
    const [workOrderId, setWorkOrderId] = useState('');
    const [filterModel, setFilterModel] = useState({
        startDate: moment().subtract(7, 'days').toDate(),
        endDate: new Date()
    });

    const columns = useMemo(() => ([
        { id: 'Customer', label: t('lblCustomer') || 'Customer' },
        { id: 'WorkOrderId', label: t('lblWorkOrderId') || 'WorkOrderId' },
        { id: 'Phone', label: t('lblPhone') || 'Phone' },
        { id: 'OpenDate', label: t('lblOpenDate') || 'OpenDate' }
    ]), [t]);
    const filterByWorkOrderLabel = t('lblFilterByWorkOrderId') === 'lblFilterByWorkOrderId'
        ? 'Filter by WorkOrderId'
        : t('lblFilterByWorkOrderId');
    const workOrderIdLabel = t('lblWorkOrderId') === 'lblWorkOrderId'
        ? 'WorkOrderId'
        : t('lblWorkOrderId');

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }

        setOpenSnackbar(false);
    };

    const handleDateChange = (name, value) => {
        setFilterModel(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const getErrorMessage = (error) => {
        if (error?.response?.data?.responseDsc) {
            return t(error.response.data.responseDsc);
        }

        if (typeof error?.response?.data === 'string') {
            return error.response.data;
        }

        return t('transactionFailed');
    };

    const fetchUrgentCustomers = () => {
        setLoading(true);
        setHasSearched(true);

        const normalizedWorkOrderId = useWorkOrderIdFilter && `${workOrderId}`.trim() !== ''
            ? `${workOrderId}`.trim()
            : null;

        getWorkOrderUrgentCustomers({
            workOrderId: normalizedWorkOrderId,
            startDate: filterModel.startDate,
            endDate: filterModel.endDate
        })
            .then((response) => {
                const parsedRows = parseUrgentCustomersResponse(response?.data);
                setRows(parsedRows);
                setPage(0);
            })
            .catch((error) => {
                setRows([]);
                setSnackbarMessage(getErrorMessage(error));
                setOpenSnackbar(true);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const pagedRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Page>
            <Backdrop className={classes.backdrop} open={loading}>
                <CircularProgress color="inherit" />
            </Backdrop>
            <Snackbar
                open={openSnackbar}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    message={snackbarMessage}
                    variant="error"
                />
            </Snackbar>
            <Container maxWidth="xl">
                <Breadcrumbs>
                    <Link color="inherit">Sirius</Link>
                    <Typography color="textPrimary">{t('WorkOrderUrgentCustomers')}</Typography>
                </Breadcrumbs>
                <br />
                <Card>
                    <CardContent>
                        <Grid container spacing={1}>
                            <Grid item xs={12} sm={3} lg={3}>
                                <ThemeProvider theme={materialTheme}>
                                    <MuiPickersUtilsProvider
                                        utils={DateFnsUtils}
                                        locale={localeMap[locale]}
                                    >
                                        <KeyboardDatePicker
                                            color="primary"
                                            inputVariant="outlined"
                                            id="work-order-urgent-customers-start-date"
                                            size="small"
                                            invalidDateMessage={t('hatali_tarih')}
                                            format="dd.MM.yyyy"
                                            fullWidth
                                            label={t('lblStartDate')}
                                            value={filterModel.startDate}
                                            onChange={date => handleDateChange('startDate', date)}
                                            KeyboardButtonProps={{
                                                'aria-label': 'change date'
                                            }}
                                        />
                                    </MuiPickersUtilsProvider>
                                </ThemeProvider>
                            </Grid>
                            <Grid item xs={12} sm={3} lg={3}>
                                <ThemeProvider theme={materialTheme}>
                                    <MuiPickersUtilsProvider
                                        utils={DateFnsUtils}
                                        locale={localeMap[locale]}
                                    >
                                        <KeyboardDatePicker
                                            color="primary"
                                            inputVariant="outlined"
                                            id="work-order-urgent-customers-end-date"
                                            size="small"
                                            invalidDateMessage={t('hatali_tarih')}
                                            format="dd.MM.yyyy"
                                            fullWidth
                                            label={t('lblEndDate')}
                                            value={filterModel.endDate}
                                            onChange={date => handleDateChange('endDate', date)}
                                            KeyboardButtonProps={{
                                                'aria-label': 'change date'
                                            }}
                                        />
                                    </MuiPickersUtilsProvider>
                                </ThemeProvider>
                            </Grid>
                            <Grid item xs={12} sm={2} lg={2}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    variant="outlined"
                                    label={workOrderIdLabel}  
                                    value={workOrderId}
                                    onChange={(event) => setWorkOrderId(event.target.value)}
                                    disabled={!useWorkOrderIdFilter}
                                />
                            </Grid>
                            <Grid item xs={12} sm={2} lg={2} className={classes.workOrderToggle}>
                                <FormControlLabel
                                    control={(
                                        <Checkbox
                                            color="primary"
                                            checked={useWorkOrderIdFilter}
                                            onChange={(event) => {
                                                const checked = event.target.checked;
                                                setUseWorkOrderIdFilter(checked);
                                                if (!checked) {
                                                    setWorkOrderId('');
                                                }
                                            }}
                                        />
                                    )}
                                    label={filterByWorkOrderLabel}
                                />
                            </Grid>
                            <Grid item xs={12} sm={2} lg={2}>
                                <Button
                                    variant="contained"
                                    className={classes.searchButton}
                                    fullWidth
                                    endIcon={<SearchIcon />}
                                    onClick={fetchUrgentCustomers}
                                >
                                    {t('search')}
                                </Button>
                            </Grid>
                        </Grid>
                    </CardContent>
                    <TableContainer className={classes.container}>
                        <Table
                            stickyHeader
                            aria-label="work-order-urgent-customers-table"
                            className={classes.table}
                            size="small"
                        >
                            <TableHead>
                                <TableRow>
                                    {columns.map((column) => (
                                        <TableCell
                                            key={column.id}
                                            align="left"
                                        >
                                            {column.label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pagedRows.map((row, index) => (
                                    <TableRow key={`${row.WorkOrderId || 'wo'}-${index}`} hover>
                                        <TableCell align="left">{row.Customer || '-'}</TableCell>
                                        <TableCell align="left">{row.WorkOrderId || '-'}</TableCell>
                                        <TableCell align="left">{row.Phone || '-'}</TableCell>
                                        <TableCell align="left">{formatDate(row.OpenDate)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={rows.length}
                        page={page}
                        onChangePage={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onChangeRowsPerPage={handleChangeRowsPerPage}
                        rowsPerPageOptions={[20, 50, 100]}
                    />
                    {hasSearched && rows.length === 0 && (
                        <Typography style={{ padding: '16px' }} color="textSecondary">
                            {t('lblNoRecord') || 'No records found for the selected date range.'}
                        </Typography>
                    )}
                </Card>
            </Container>
        </Page>
    );
};

export default WorkOrderUrgentCustomers;
